// @ts-check
/**
 * UI category labels — display-side helpers for the 9 canonical
 * provider categories (Block 6.F.1; per design plan §3.3).
 *
 * Verbatim port of peace-hub/src/utils/constants.js, plus a slug-to-
 * display-name map because peace-os DB stores slugs (kebab-case) and
 * UI labels are keyed by display-name.
 *
 * §m: This file is `uiCategoryLabels.js`, NOT `categoryConstants.js`,
 * to disambiguate from the authoritative DB lookups
 * (provider_categories table; api/_shared/repos/lookups.ts). UI labels
 * are wholly-derivative; the DB is the source. If a category is added
 * or renamed, update the DB first; this file follows.
 */

/** @type {Record<string, string>} */
export const CATEGORY_ICONS = {
  'Care Facility / Housing': '🏠',
  'Medical Practice': '🩺',
  'Home & Personal Services': '🤝',
  'Professional Services': '📋',
  'Government and Non-Profit': '🏛️',
  'Transportation': '🚗',
  'Hospital & Health System': '🏥',
  'Activities & Programs': '⭐',
  'Home Health & Hospice': '💚',
}

/** @type {Record<string, string>} */
export const CATEGORY_SHORT = {
  'Care Facility / Housing': 'Care Facilities & Housing',
  'Medical Practice': 'Medical',
  'Home & Personal Services': 'Home Services',
  'Professional Services': 'Professional Services',
  'Government and Non-Profit': 'Government & Non-Profit',
  'Transportation': 'Transportation',
  'Hospital & Health System': 'Hospitals',
  'Activities & Programs': 'Activities & Programs',
  'Home Health & Hospice': 'Home Health & Hospice',
}

/** @type {Record<string, { border: string, label: string }>} */
export const CATEGORY_COLORS = {
  'Care Facility / Housing':  { border: '#e8b4b8', label: '#c47a80' },
  'Medical Practice':         { border: '#94b8d0', label: '#5a8aaa' },
  'Home & Personal Services': { border: '#dcc88a', label: '#a89450' },
  'Professional Services':    { border: '#b0a8c8', label: '#7a6ea0' },
  'Government and Non-Profit':{ border: '#a0b8a0', label: '#6a886a' },
  'Transportation':           { border: '#d4a0a0', label: '#a86868' },
  'Hospital & Health System': { border: '#88c0c8', label: '#508890' },
  'Activities & Programs':    { border: '#e0b878', label: '#b08840' },
  'Home Health & Hospice':    { border: '#90c0a0', label: '#588868' },
}

/**
 * X.6.5 — soft tinted chip colors for the "Browse by category" tiles + the
 * directory result-card icon chips (design-refresh prototype's CAT_LOOK).
 * Per-category identity data (kept here, like CATEGORY_COLORS, so the restyled
 * component files carry no raw hexes). `bg` is the chip fill, `fg` the icon.
 *
 * @type {Record<string, { bg: string, fg: string }>}
 */
export const CATEGORY_TILE_COLORS = {
  // UX-13 direction (2026-07-02): the two blue identities retire; Medical
  // joins the health greens, Home & Personal joins the home golds.
  'Medical Practice':         { bg: '#E4EFE9', fg: '#2E6E5B' },
  'Activities & Programs':    { bg: '#F6EEDD', fg: '#9A7C42' },
  'Government and Non-Profit':{ bg: '#EAF1ED', fg: '#456A60' },
  'Care Facility / Housing':  { bg: '#F3E9D6', fg: '#8A6A3A' },
  'Hospital & Health System': { bg: '#E4EFE9', fg: '#2E6E5B' },
  'Professional Services':    { bg: '#EAF1ED', fg: '#1B4332' },
  'Home & Personal Services': { bg: '#F3E9D6', fg: '#8A6A3A' },
  'Home Health & Hospice':    { bg: '#E4EFE9', fg: '#2E6E5B' },
  'Transportation':           { bg: '#F6EEDD', fg: '#9A7C42' },
}

/** @type {Record<string, string>} */
export const FILTER_LABELS = {
  categories: 'Category',
  subcategories: 'Service Type',
  specialties: 'Specialty / Condition',
  // Phase 1.9.1 — internal DROPDOWN_FILTERS key 'highlights' renders as
  // "Features" in the picker bottom-sheet title (mirrors the chip label
  // change at FilterBar.jsx). Internal key kept for minimum-diff per
  // opener §locked decision #1.
  highlights: 'Features',
  // Phase 1.9.3 — Service Model top-level filter (mirrors Specialty pattern;
  // cascades from r.service_delivery_model with the 9 atomic single-value
  // entries per migration 0068's empirical vocabulary scan).
  service_models: 'Delivery method',
  // Payment type pill (mirrors the Specialty cascade; sourced from
  // provider_organizations.payment_types jsonb string[]).
  payment_types: 'Payment type',
  counties: 'County',
  // Individuals directory (people view) — contact specialties facet, cascaded
  // from the contacts' comma-joined controlled-vocab specialty tokens. Separate
  // key from the org `specialties` facet (different option source).
  contact_specialties: 'Specialties',
}

/**
 * Slug → display-name map. peace-os DB seeds 9 canonical categories
 * keyed by kebab-slug (db/migrations/0012_create_provider_categories.sql);
 * UI labels are keyed by display-name.
 *
 * @type {Record<string, string>}
 */
export const CATEGORY_SLUG_TO_DISPLAY = {
  'care-facility-housing': 'Care Facility / Housing',
  'medical-practice': 'Medical Practice',
  'home-personal-services': 'Home & Personal Services',
  'professional-services': 'Professional Services',
  'government-non-profit': 'Government and Non-Profit',
  'transportation': 'Transportation',
  'hospital-health-system': 'Hospital & Health System',
  'activities-programs': 'Activities & Programs',
  'home-health-hospice': 'Home Health & Hospice',
}

/**
 * Resolve a slug to its display-name; returns null if unknown.
 * @param {string} slug
 * @returns {string | null}
 */
export function categoryDisplayFromSlug(slug) {
  return CATEGORY_SLUG_TO_DISPLAY[slug] ?? null
}

/**
 * Reverse map: display-name → slug. Derived from CATEGORY_SLUG_TO_DISPLAY
 * to keep a single source of truth. Block 6.G.2 B1: SearchBar tile grid
 * passes display names to onCategorySelect; the filter pipeline keys on
 * slugs (r.category_slug). Converting display→slug at the SearchBar
 * boundary keeps the state shape consistent with the FilterBar dropdown
 * path (which already stores slugs).
 *
 * @type {Record<string, string>}
 */
export const CATEGORY_DISPLAY_TO_SLUG = Object.fromEntries(
  Object.entries(CATEGORY_SLUG_TO_DISPLAY).map(([slug, display]) => [display, slug]),
)

/**
 * Resolve a display-name to its slug; returns null if unknown.
 * @param {string} display
 * @returns {string | null}
 */
export function categorySlugFromDisplay(display) {
  return CATEGORY_DISPLAY_TO_SLUG[display] ?? null
}

/**
 * @returns {string}
 */
export function getGreeting() {
  const h = new Date().getHours()
  if (h < 12) return 'Good morning ☀️'
  if (h < 17) return 'Good afternoon 🌿'
  return 'Good evening 🌙'
}

/**
 * @param {string | null | undefined} phone
 * @returns {string}
 */
export function formatPhone(phone) {
  if (!phone) return ''
  return phone.replace(/[^\d+]/g, '')
}
